import type { DivisionConfig, FlightConfig, FlightingShape } from "./flightPlacementTypes";

function cloneFlighting(flighting: FlightingShape): FlightingShape {
    return JSON.parse(JSON.stringify(flighting));
}

function createDivisionFromTemplate(
    template: DivisionConfig | undefined,
    newIndex: number,
    flightPricing?: boolean
): DivisionConfig {
    if (!template) {
        return flightPricing
            ? { index: newIndex, divisionName: `Division ${newIndex + 1}`, divisionPrice: 0 }
            : { index: newIndex, divisionName: `Division ${newIndex + 1}` };
    }

    const division: DivisionConfig = {
        index: newIndex,
        divisionName: `Division ${newIndex + 1}`,
    };

    Object.keys(template).forEach((key) => {
        if (key === "index" || key === "divisionName") return;
        (division as unknown as Record<string, unknown>)[key] = (template as unknown as Record<string, unknown>)[key];
    });

    if (flightPricing && !("divisionPrice" in division)) {
        division.divisionPrice = 0;
    }

    return division;
}

function createFlightFromTemplate(
    template: FlightConfig | undefined,
    divisionIndex: number,
    flightIndex: number,
    flightPricing?: boolean
): FlightConfig {
    if (!template) {
        return flightPricing
            ? { divisionIndex, flightName: "Flight 1", index: flightIndex, flightPrice: 0 }
            : { divisionIndex, flightName: "Flight 1", index: flightIndex };
    }

    const flight: FlightConfig = {
        divisionIndex,
        flightName: "New Flight",
        index: flightIndex,
    };

    Object.keys(template).forEach((key) => {
        if (["index", "divisionIndex", "flightName", "players", "divisionOnly"].includes(key)) return;
        (flight as unknown as Record<string, unknown>)[key] = (template as unknown as Record<string, unknown>)[key];
    });

    if (flightPricing && !("flightPrice" in flight)) {
        flight.flightPrice = 0;
    }

    return flight;
}

function reindexDivisions(divisions: DivisionConfig[]): DivisionConfig[] {
    return divisions.map((division, index) => ({ ...division, index }));
}

function reindexFlights(flights: FlightConfig[]): FlightConfig[] {
    return flights.map((flight, index) => ({ ...flight, index }));
}

/** Append a division; seeds a default flight when flights are enabled. */
export function addDivision(flighting: FlightingShape, flightPricing?: boolean): FlightingShape {
    const updated = cloneFlighting(flighting);
    const divisions = (updated.divisions ?? []) as DivisionConfig[];
    const newIndex = divisions.length;
    const template = divisions[divisions.length - 1];
    updated.divisions = [...divisions, createDivisionFromTemplate(template, newIndex, flightPricing)];

    const flightsEnabled = !!(updated.props as Record<string, unknown> | undefined)?.flights;
    if (flightsEnabled) {
        const flights = (updated.flights ?? []) as FlightConfig[];
        const templateFlight =
            flights.find((flight) => flight.divisionIndex === newIndex - 1) ?? flights[flights.length - 1];
        updated.flights = [
            ...flights,
            createFlightFromTemplate(templateFlight, newIndex, flights.length, flightPricing),
        ];
    }

    return updated;
}

/** Remove a division and re-index remaining divisions and flights. Requires at least one division. */
export function deleteDivision(flighting: FlightingShape, divisionIndex: number): FlightingShape {
    const updated = cloneFlighting(flighting);
    const divisions = (updated.divisions ?? []) as DivisionConfig[];
    if (divisions.length <= 1) return updated;

    updated.divisions = reindexDivisions(
        divisions.filter((division) => division.index !== divisionIndex)
    );

    const flights = (updated.flights ?? []) as FlightConfig[];
    updated.flights = reindexFlights(
        flights
            .filter((flight) => flight.divisionIndex !== divisionIndex)
            .map((flight) => ({
                ...flight,
                divisionIndex: flight.divisionIndex > divisionIndex ? flight.divisionIndex - 1 : flight.divisionIndex,
            }))
    );

    return updated;
}

/** Append a flight within a division. */
export function addFlight(
    flighting: FlightingShape,
    divisionIndex: number,
    templateFlight?: FlightConfig,
    flightPricing?: boolean
): FlightingShape {
    const updated = cloneFlighting(flighting);
    const flights = (updated.flights ?? []) as FlightConfig[];
    const divisionFlights = flights.filter((flight) => flight.divisionIndex === divisionIndex);
    const source = templateFlight ?? divisionFlights[divisionFlights.length - 1];

    updated.flights = [
        ...flights,
        createFlightFromTemplate(source, divisionIndex, flights.length, flightPricing),
    ];

    return updated;
}

/** Remove a flight by global flight index and re-index remaining flights. */
export function deleteFlight(flighting: FlightingShape, flightIndex: number): FlightingShape {
    const updated = cloneFlighting(flighting);
    const flights = (updated.flights ?? []) as FlightConfig[];
    updated.flights = reindexFlights(flights.filter((flight) => flight.index !== flightIndex));
    return updated;
}

/** Whether the given division is the last flight row in its division (for showing add button). */
export function isLastFlightInDivision(
    flights: FlightConfig[],
    divisionIndex: number,
    flightIndex: number
): boolean {
    const divisionFlightIndices = flights
        .filter((flight) => flight.divisionIndex === divisionIndex)
        .map((flight) => flight.index);
    if (divisionFlightIndices.length === 0) return false;
    return flightIndex === Math.max(...divisionFlightIndices);
}
